import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Collapse,
  Input,
  InputGroup,
  InputLeftElement,
  useColorModeValue,
  HStack,
  Badge,
  IconButton as ChakraIconButton,
  Icon,
  InputRightElement,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  VStack,
  Button,
  ButtonGroup,
  Portal,
  Tag,
  TagLabel,
  TagCloseButton,
} from '@chakra-ui/react';
import { FiChevronDown, FiChevronRight, FiPlus, FiEdit2, FiCheck, FiClock, FiFolder, FiTrash2, FiAlertCircle } from 'react-icons/fi';
import { TaskItem } from './TaskItem';
import { Task } from '../../../types/task';
import { addDays, format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { getProjectColor } from '../../../utils/colorUtils';

interface GroupBaseProps {
  title: string;
  tasks: Task[];
  isOpen: boolean;
  onToggle: () => void;
  onSelectTask: (task: Task) => void;
  onToggleDone: (task: Task, done: boolean) => void;
  onDeleteTask: (id: string) => void;
  onQuickAddTask: (title: string, options?: { projectId?: string | null, startAt?: string }) => void;
  addButtonProjectId?: string | null;
  showAddButton?: boolean;
  customHeader?: React.ReactNode;
  customTaskRenderer?: (task: Task, onSelect: (task: Task) => void, onToggleDone: (task: Task, done: boolean) => void, onDelete: (id: string) => void) => React.ReactNode;
  groupType?: 'project' | 'time' | 'default';
  projects?: { id: string; title: string; }[];
  projectId?: string | null;
}

export const GroupBase = ({ 
  title,
  tasks,
  isOpen,
  onToggle,
  onSelectTask,
  onToggleDone,
  onDeleteTask,
  onQuickAddTask,
  addButtonProjectId = null,
  showAddButton = true,
  customHeader = null,
  customTaskRenderer = (task: Task, onSelect, onToggleDone, onDelete) => (
    <TaskItem 
      task={task} 
      onSelect={onSelect}
      onToggleDone={onToggleDone}
      onDelete={onDelete}
    />
  ),
  groupType = 'default',
  projects = [],
  projectId = null,
}: GroupBaseProps & { groupType?: 'project' | 'time' | 'default' }) => {
  const [showCompleted, setShowCompleted] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddTitle, setQuickAddTitle] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selectedStartAt, setSelectedStartAt] = useState<string | undefined>(undefined);
  const borderColor = useColorModeValue("gray.100", "gray.700");
  const { t } = useTranslation(['v0/tasks', 'v0/common']);

  const selectedProject = projects.find(p => p.id === selectedProjectId);

  const formattedSelectedStartAt = selectedStartAt 
    ? format(new Date(selectedStartAt), 'MM/dd')
    : undefined;

  const handleQuickAddSubmit = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (quickAddTitle.trim()) {
      onQuickAddTask(quickAddTitle.trim(), { 
        projectId: selectedProjectId,
        startAt: selectedStartAt
      });
      
      setQuickAddTitle('');
      setSelectedProjectId(null);
      setSelectedStartAt(undefined);
    }
  };

  const handleAddButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (quickAddTitle.trim()) {
      handleQuickAddSubmit();
    } else {
      setShowQuickAdd(!showQuickAdd);
      setSelectedProjectId(null);
      setSelectedStartAt(undefined);
    }
  };

  const handleTimeSelect = (days: number) => {
    const startAt = addDays(new Date(), days).toISOString();
    setSelectedStartAt(startAt);
  };

  const handleProjectSelect = (projectId: string) => {
    setSelectedProjectId(projectId);
  };

  // Separate tasks into pending and completed
  const pendingTasks = tasks.filter(task => !task.done);
  const completedTasks = tasks.filter(task => task.done);
  const VISIBLE_COMPLETED_THRESHOLD = 3;
  const visibleCompletedTasks = completedTasks.slice(0, VISIBLE_COMPLETED_THRESHOLD);
  const hiddenCompletedTasks = completedTasks.slice(VISIBLE_COMPLETED_THRESHOLD);
  const showToggleButton = hiddenCompletedTasks.length > 0;

  // Get project color if applicable - Use title prop
  const projectColor = groupType === 'project' && title ? getProjectColor(title) : null;
  // Log calculated color for debugging
  console.log(`[GroupBase] title: ${title}, groupType: ${groupType}, projectId: ${projectId}, calculatedColor: ${projectColor}`); 
  const titleColor = useColorModeValue("gray.700", "gray.200");
  const iconColor = useColorModeValue("gray.500", "gray.400");

  return (
    <Box 
      mb={2} 
      borderTopWidth="1px" 
      borderColor={borderColor}
    >
      <Flex 
        align="center" 
        justify="space-between" 
        py={2} 
        px={3} 
        cursor="pointer"
        _hover={{ bg: useColorModeValue('gray.50', 'gray.750') }}
        transition="background 0.1s"
        userSelect="none"
      >
        {customHeader ? (
          <Flex flex="1" align="center">
             {customHeader}
          </Flex>
        ) : (
          <div className="flex items-center flex-1 cursor-pointer" onClick={onToggle}>
            <span 
              className="mr-2 h-4 w-4 flex-shrink-0" 
              style={{ color: projectColor ? `${projectColor} !important` : iconColor }}
            >
              {(groupType === 'project' && projectColor) ? <FiFolder /> : (isOpen ? <FiChevronDown /> : <FiChevronRight />)} 
            </span>
            <span 
              className={`font-semibold text-lg truncate ${!projectColor ? 'text-gray-700 dark:text-gray-200' : ''}`}
              style={{ color: projectColor ? `${projectColor} !important` : titleColor }}
            >
              {title}
            </span>
          </div>
        )}
        
        <HStack spacing={2}>
          <Badge colorScheme="gray" variant="subtle" borderRadius="full" px={2} py={0.5} fontSize="xs" fontWeight="medium">
            {completedTasks.length}/{tasks.length}
          </Badge>
          {showAddButton && (
            <ChakraIconButton
              icon={<FiPlus />}
              aria-label={t('v0/tasks:actions.addTask')}
              variant="ghost"
              size="sm"
              colorScheme="pink"
              onClick={handleAddButtonClick}
            />
          )}
        </HStack>
      </Flex>
      
      <Collapse in={showQuickAdd} animateOpacity>
        <Box p={3} bg={useColorModeValue('white', 'gray.800')} borderBottomWidth={isOpen ? "1px" : "0"} borderColor={borderColor}>
          <form onSubmit={handleQuickAddSubmit}>
            <InputGroup size="md">
              <Input
                placeholder={t('v0/tasks:quickAdd')}
                value={quickAddTitle}
                onChange={(e) => setQuickAddTitle(e.target.value)}
                autoFocus
                pr={quickAddTitle.trim() ? (groupType === 'project' || groupType === 'time' ? '7rem' : '4.5rem') : (groupType === 'project' || groupType === 'time' ? '4.5rem' : '2.5rem')}
                borderRadius="md"
                bg={useColorModeValue('white', 'gray.900')}
                _focus={{
                  borderColor: "pink.400",
                  boxShadow: "0 0 0 1px pink.400"
                }}
              />
              <InputLeftElement>
                <Box as={FiEdit2} color="pink.400" />
              </InputLeftElement>
              <InputRightElement width="auto" pr={2}>
                <HStack spacing={1} justify="flex-end">
                  {selectedProject && (
                    <Tag size="sm" variant='subtle' colorScheme='purple' borderRadius='full'>
                      <TagLabel>{selectedProject.title}</TagLabel>
                      <TagCloseButton onClick={() => setSelectedProjectId(null)} />
                    </Tag>
                  )}
                  {formattedSelectedStartAt && (
                    <Tag size="sm" variant='subtle' colorScheme='teal' borderRadius='full'>
                      <TagLabel>{formattedSelectedStartAt}</TagLabel>
                      <TagCloseButton onClick={() => setSelectedStartAt(undefined)} />
                    </Tag>
                  )}
                  {quickAddTitle.trim() && (
                    <ChakraIconButton
                      h="1.75rem"
                      size="sm"
                      icon={<FiCheck />}
                      aria-label={t('v0/common:actions.submit')}
                      onClick={handleQuickAddSubmit}
                      colorScheme="pink"
                      variant="ghost"
                    />
                  )}
                  {groupType !== 'project' && (
                    <Popover placement="bottom-end" strategy="fixed" isLazy>
                      <PopoverTrigger>
                        <ChakraIconButton
                          h="1.75rem"
                          size="sm"
                          icon={<FiFolder />}
                          aria-label={t('v0/tasks:form.projectField')}
                          colorScheme="pink"
                          variant="ghost"
                          isDisabled={!!selectedProjectId}
                        />
                      </PopoverTrigger>
                      <Portal>
                        <PopoverContent 
                          width="200px" 
                          bg={useColorModeValue('white', 'gray.700')} 
                          borderColor={useColorModeValue('gray.200', 'gray.600')}
                          zIndex={1400}
                        >
                          <PopoverBody py={2}>
                            <VStack align="stretch" spacing={1}>
                              {projects?.map(project => (
                                <Button
                                  key={project.id}
                                  size="sm"
                                  variant="ghost"
                                  justifyContent="flex-start"
                                  onClick={() => handleProjectSelect(project.id)}
                                  py={1}
                                  px={3}
                                  height="32px"
                                  borderRadius="md"
                                  _hover={{ bg: useColorModeValue('gray.100', 'gray.600') }}
                                  _focus={{ boxShadow: 'none' }}
                                >
                                  {project.title}
                                </Button>
                              ))}
                            </VStack>
                          </PopoverBody>
                        </PopoverContent>
                      </Portal>
                    </Popover>
                  )}
                  {groupType !== 'time' && (
                    <Popover placement="bottom-end" strategy="fixed" isLazy>
                      <PopoverTrigger>
                        <ChakraIconButton
                          h="1.75rem"
                          size="sm"
                          icon={<FiClock />}
                          aria-label={t('v0/tasks:form.dates.startDate')}
                          colorScheme="pink"
                          variant="ghost"
                          isDisabled={!!selectedStartAt}
                        />
                      </PopoverTrigger>
                      <Portal>
                        <PopoverContent 
                          width="160px" 
                          bg={useColorModeValue('white', 'gray.700')} 
                          borderColor={useColorModeValue('gray.200', 'gray.600')}
                          zIndex={1400}
                        >
                          <PopoverBody py={2}>
                            <VStack align="stretch" spacing={1}>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                justifyContent="flex-start" 
                                onClick={() => handleTimeSelect(0)}
                                py={1}
                                px={3}
                                height="32px"
                                borderRadius="md"
                                _hover={{ bg: useColorModeValue('gray.100', 'gray.600') }}
                                _focus={{ boxShadow: 'none' }}
                              >
                                {t('v0/tasks:form.dates.today')}
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                justifyContent="flex-start" 
                                onClick={() => handleTimeSelect(1)}
                                py={1}
                                px={3}
                                height="32px"
                                borderRadius="md"
                                _hover={{ bg: useColorModeValue('gray.100', 'gray.600') }}
                                _focus={{ boxShadow: 'none' }}
                              >
                                {t('v0/tasks:form.dates.tomorrow')}
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                justifyContent="flex-start" 
                                onClick={() => handleTimeSelect(2)}
                                py={1}
                                px={3}
                                height="32px"
                                borderRadius="md"
                                _hover={{ bg: useColorModeValue('gray.100', 'gray.600') }}
                                _focus={{ boxShadow: 'none' }}
                              >
                                {t('v0/tasks:form.dates.dayAfterTomorrow')}
                              </Button>
                            </VStack>
                          </PopoverBody>
                        </PopoverContent>
                      </Portal>
                    </Popover>
                  )}
                </HStack>
              </InputRightElement>
            </InputGroup>
          </form>
        </Box>
      </Collapse>
      
      {/* Render Pending Tasks */} 
      <Collapse in={isOpen} animateOpacity unmountOnExit>
         <Box overflow="visible" pt={pendingTasks.length > 0 ? 1 : 0}>
           {pendingTasks.length > 0 ? (
             pendingTasks.map(task => (
              customTaskRenderer ? (
                 <React.Fragment key={`pending-${task.id}`}> 
                  {customTaskRenderer(task, onSelectTask, onToggleDone, onDeleteTask)}
                </React.Fragment>
              ) : (
                <TaskItem 
                  key={task.id} 
                  task={task} 
                  onSelect={onSelectTask}
                  onToggleDone={onToggleDone}
                  onDelete={onDeleteTask}
                />
              )
            ))
          ) : (
              !completedTasks.length && (
            <Box py={4} textAlign="center">
              <Text color="gray.500" fontSize="sm">{t('v0/tasks:emptyState.noTasksInGroup')}</Text>
            </Box>
              )
          )}
        </Box>
      </Collapse>

       {/* Render Visible Completed Tasks */} 
       {isOpen && visibleCompletedTasks.length > 0 && (
         <Box overflow="visible" pt={1} borderTopWidth={pendingTasks.length > 0 ? "1px" : "0"} borderColor={borderColor}>
           {visibleCompletedTasks.map(task => (
             customTaskRenderer ? (
               <React.Fragment key={`visible-completed-${task.id}`}> 
                 {customTaskRenderer(task, onSelectTask, onToggleDone, onDeleteTask)}
               </React.Fragment>
             ) : (
               <TaskItem
                 key={`visible-completed-${task.id}`}
                 task={task}
                 onSelect={onSelectTask}
                 onToggleDone={onToggleDone}
                 onDelete={onDeleteTask}
               />
             )
           ))}
         </Box>
       )}

       {/* Render Toggle Button if needed */} 
       {isOpen && showToggleButton && (
           <Flex 
              justify="center" 
              py={1.5} 
              px={3} 
              borderTopWidth="1px" 
              borderColor={borderColor}
              borderBottomWidth={showCompleted ? '1px' : '0'} // Add border only when expanded
              bg={useColorModeValue('white', 'gray.800')} // Keep background for button area
           >
             <Button 
               size="xs" 
               variant="ghost" 
               colorScheme="gray" 
               onClick={() => setShowCompleted(!showCompleted)} 
               width="full" 
               leftIcon={showCompleted ? <FiChevronDown /> : <FiChevronRight />}
               fontWeight="medium"
               color="gray.500"
             >
               {showCompleted 
                  ? t('v0/tasks:actions.hideCompleted') 
                  : t('v0/tasks:actions.showMoreCompleted', { count: hiddenCompletedTasks.length })}
             </Button>
           </Flex>
       )}

       {/* Render Hidden Completed Tasks */} 
       {isOpen && showToggleButton && (
           <Collapse in={showCompleted} animateOpacity unmountOnExit>
             <Box overflow="visible" pt={1} pb={1}>
                 {hiddenCompletedTasks.map(task => (
                     customTaskRenderer ? (
                         <React.Fragment key={`hidden-completed-${task.id}`}> 
                             {customTaskRenderer(task, onSelectTask, onToggleDone, onDeleteTask)}
                         </React.Fragment>
                     ) : (
                         <TaskItem
                             key={`hidden-completed-${task.id}`}
                             task={task}
                             onSelect={onSelectTask}
                             onToggleDone={onToggleDone}
                             onDelete={onDeleteTask}
                         />
                     )
                 ))}
             </Box>
           </Collapse>
       )}
    </Box>
  );
}; 